/**
 * Hauptanwendung für Mietwerkstatt Frontend
 * Mit Routing, Authentication und Layout
 */

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { UserRole } from "./types/api-types";
import Layout from "./components/Layout/Layout";
import Login from "./components/Auth/Login";
import DashboardUltraClean from "./components/Dashboard/Dashbaord";
import CustomersPage from "./pages/CustomersPage";
import ArticlesPage from "./pages/ArticlesPage";
import InvoicesPage from "./pages/InvoicesPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/ProfilePage";
import CompanySettingsPage from "./pages/settings/CompanySettingsPage";
import UserSettingsPage from "./pages/settings/UserSettingsPage";
import PricingSettingsPage from "./pages/settings/PricingSettingsPage";
import BusinessSettingsPage from "./pages/settings/BusinessSettingsPage";
import ApiKeySettingsPage from "./pages/settings/ApiKeySettingsPage";
import SystemSettingsPage from "./pages/settings/SystemSettingsPage";
import LegacyImportSettingsPage from "./pages/settings/LegacyImportSettingsPage";
import ChatSettingsPage from "./pages/settings/ChatSettingsPage";
import BlockedContactsPage from "./pages/settings/BlockedContactsPage";
import AiSettingsPage from "./pages/settings/AiSettingsPage";
import RentalObjectsPage from "./pages/RentalObjectsPage";
import BundleManagementPage from "./pages/BundleManagementPage";
import RevenueAnalyticsPage from "./pages/RevenueAnalyticsPage";
import "./App.css";

// Protected Route Component
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requiredRoles?: UserRole[];
}> = ({ children, requiredRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Lade Anwendung...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  if (requiredRoles && (!user || !requiredRoles.includes(user.role))) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// App Routes
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardUltraClean />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rental-objects"
        element={
          <ProtectedRoute>
            <RentalObjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/*"
        element={
          <ProtectedRoute>
            <CustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/articles/*"
        element={
          <ProtectedRoute>
            <ArticlesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/articles/bundles"
        element={
          <ProtectedRoute>
            <BundleManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/*"
        element={
          <ProtectedRoute>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/*"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/company"
        element={
          <ProtectedRoute>
            <CompanySettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/users"
        element={
          <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.MANAGER]}>
            <UserSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/pricing"
        element={
          <ProtectedRoute>
            <PricingSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/business"
        element={
          <ProtectedRoute>
            <BusinessSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/api-keys"
        element={
          <ProtectedRoute>
            <ApiKeySettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/system"
        element={
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
            <SystemSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/legacy-import"
        element={
          <ProtectedRoute>
            <LegacyImportSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/chat"
        element={
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
            <ChatSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/blocked-contacts"
        element={
          <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.MANAGER]}>
            <BlockedContactsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/ai"
        element={
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
            <AiSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/revenue-analytics"
        element={
          <ProtectedRoute>
            <RevenueAnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppRoutes />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
